import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Form, InputGroup, Modal, Spinner, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { adminApi, extractError } from '../api/client';
import { useToast } from '../contexts/ToastContext';
import { PartyBadge } from './PartyBadge';

/**
 * CandidateRaceAssignments — manages the many-to-many binding between a
 * candidate and any number of races. Each binding also carries per-race
 * flags (incumbent, ballot_status, is_visible) that admins can tweak.
 *
 * Design:
 *   - Read-only summary table with an "×" button per row for unassign.
 *   - "Assign to a race" combobox at the bottom — searches races across
 *     ALL elections (not scoped to any one election). Debounced 300ms.
 *   - "Set as incumbent" toggle appears on the confirm dialog so the
 *     admin can decide the per-race attribute without re-editing later.
 *
 * Props:
 *   candidateId  — required, string
 *   participations — array from the candidate GET response
 *   defaultParty  — the candidate's canonical party, used as the per-race
 *                   default when creating a new binding. Empty string OK.
 *   onChange()    — called after any assign/unassign; parent should
 *                   re-fetch the candidate to refresh the participations.
 */
export default function CandidateRaceAssignments({
  candidateId,
  participations = [],
  defaultParty = '',
  onChange,
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');
  const [pickerResults, setPickerResults] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [confirmRace, setConfirmRace] = useState(null);
  const [confirmIncumbent, setConfirmIncumbent] = useState(false);
  const [confirmPartyOverride, setConfirmPartyOverride] = useState('');

  // Set of race_ids already bound — used to grey-out picker options.
  const assignedRaceIds = useMemo(() => new Set(
    (participations || []).map(p => p.race_id),
  ), [participations]);

  // Debounced search
  useEffect(() => {
    if (!pickerOpen) return undefined;
    let cancelled = false;
    const handle = setTimeout(async () => {
      setPickerLoading(true);
      try {
        const res = await adminApi.get('/admin/races', {
          params: { q: pickerQuery, limit: 25 },
        });
        if (!cancelled) setPickerResults(res.data?.items || []);
      } catch (e) {
        if (!cancelled) toast.error(extractError(e));
      } finally {
        if (!cancelled) setPickerLoading(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [pickerQuery, pickerOpen, toast]);

  const openPicker = () => {
    setPickerQuery('');
    setPickerResults([]);
    setPickerOpen(true);
  };

  const chooseRace = (race) => {
    setConfirmRace(race);
    setConfirmIncumbent(false);
    setConfirmPartyOverride('');
  };

  const applyAssignment = async () => {
    if (!confirmRace) return;
    setBusy(true);
    try {
      const body = { incumbent: confirmIncumbent };
      // Only send party override if admin typed something. Otherwise the
      // backend copies from candidate.party.
      if (confirmPartyOverride.trim()) {
        body.party = confirmPartyOverride.trim();
      }
      await adminApi.post(
        `/admin/candidates/${candidateId}/races/${confirmRace.id}`,
        body,
      );
      toast.success(`Assigned to ${confirmRace.label}`);
      setConfirmRace(null);
      setPickerOpen(false);
      if (onChange) await onChange();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setBusy(false);
    }
  };

  const unassign = async (participation) => {
    if (!window.confirm(`Remove ${participation.office_name}${participation.race_district ? ` District ${participation.race_district}` : ''} from ${participation.election_name}?`)) return;
    setBusy(true);
    try {
      await adminApi.delete(
        `/admin/candidates/${candidateId}/races/${participation.race_id}`,
      );
      toast.success('Race assignment removed');
      if (onChange) await onChange();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="mb-3" data-testid="candidate-race-assignments">
      <Card.Header className="d-flex justify-content-between align-items-center py-2">
        <strong>Race Assignments</strong>
        <Button
          size="sm"
          variant="outline-primary"
          onClick={openPicker}
          disabled={busy}
          data-testid="assign-race-btn"
        >
          <i className="bi bi-plus-lg me-1" />
          Assign to a race
        </Button>
      </Card.Header>
      <Card.Body className="p-0">
        {participations.length === 0 ? (
          <div className="text-center text-muted py-4 small" data-testid="no-race-assigned">
            <i className="bi bi-info-circle me-1" />
            No race assigned. This candidate profile is not tied to any race yet — that&apos;s OK.
          </div>
        ) : (
          <Table size="sm" className="mb-0 align-middle">
            <thead className="text-muted small">
              <tr>
                <th>Election</th>
                <th>Race</th>
                <th>Party</th>
                <th>Status</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {participations.map((p) => (
                <tr key={p.race_candidate_id} data-testid={`race-participation-row-${p.race_candidate_id}`}>
                  <td>
                    <Link to={`/admin/elections/${p.election_id}`} className="text-decoration-none">
                      {p.election_name || '—'}
                    </Link>
                    <div className="text-muted small">{p.election_date}</div>
                  </td>
                  <td>
                    {p.office_name}{p.race_district ? ` (District ${p.race_district})` : ''}
                    {p.incumbent && <Badge bg="warning" text="dark" className="ms-2 small">Incumbent</Badge>}
                  </td>
                  <td><PartyBadge party={p.party_label} /></td>
                  <td>
                    <Badge bg={
                      p.result_status === 'won_primary' ? 'success'
                      : p.result_status === 'lost_primary' ? 'dark'
                      : p.result_status === 'unopposed' ? 'info'
                      : 'secondary'
                    } className="small">
                      {p.result_status || 'pending'}
                    </Badge>
                    {!p.is_visible && (
                      <i
                        className="bi bi-eye-slash text-muted ms-2"
                        title="Hidden from public API"
                      />
                    )}
                  </td>
                  <td className="text-end">
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => unassign(p)}
                      disabled={busy}
                      data-testid={`unassign-race-btn-${p.race_candidate_id}`}
                      title="Remove race assignment"
                    >
                      <i className="bi bi-x-lg" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>

      {/* --- Race picker modal --- */}
      <Modal show={pickerOpen} onHide={() => setPickerOpen(false)} size="lg" data-testid="race-picker-modal">
        <Modal.Header closeButton>
          <Modal.Title>Assign candidate to a race</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <InputGroup className="mb-3">
            <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
            <Form.Control
              placeholder="Search by office, district, or election name…"
              value={pickerQuery}
              onChange={(e) => setPickerQuery(e.target.value)}
              autoFocus
              data-testid="race-picker-search"
            />
          </InputGroup>
          {pickerLoading && (
            <div className="text-center py-3"><Spinner size="sm" /></div>
          )}
          {!pickerLoading && pickerResults.length === 0 && (
            <div className="text-center text-muted py-3 small">
              {pickerQuery ? 'No races match your search.' : 'Start typing to search races…'}
            </div>
          )}
          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            {pickerResults.map((r) => {
              const already = assignedRaceIds.has(r.id);
              return (
                <button
                  type="button"
                  key={r.id}
                  className={`list-group-item list-group-item-action ${already ? 'text-muted' : ''}`}
                  onClick={() => !already && chooseRace(r)}
                  disabled={already}
                  data-testid={`race-option-${r.id}`}
                  style={{ display: 'block', width: '100%', textAlign: 'left' }}
                >
                  <div>{r.label}</div>
                  <div className="text-muted small">
                    {r.election_date} · {r.election_type || 'election'}
                    {r.jurisdiction_name ? ` · ${r.jurisdiction_name}` : ''}
                    {already && <Badge bg="secondary" className="ms-2">Already assigned</Badge>}
                  </div>
                </button>
              );
            })}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setPickerOpen(false)}>Cancel</Button>
        </Modal.Footer>
      </Modal>

      {/* --- Confirm & configure the assignment --- */}
      <Modal show={!!confirmRace} onHide={() => setConfirmRace(null)} data-testid="race-confirm-modal">
        <Modal.Header closeButton>
          <Modal.Title>Confirm race assignment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {confirmRace && (
            <>
              <Alert variant="light" className="mb-3 small">
                Assigning candidate to <strong>{confirmRace.label}</strong>.
              </Alert>
              <Form.Group className="mb-3">
                <Form.Label>Party for this race (optional)</Form.Label>
                <Form.Control
                  placeholder={defaultParty ? `Defaults to "${defaultParty}"` : "Same as candidate's party"}
                  value={confirmPartyOverride}
                  onChange={(e) => setConfirmPartyOverride(e.target.value)}
                  data-testid="race-confirm-party-input"
                />
                <Form.Text className="text-muted">
                  Leave blank to inherit the candidate&apos;s canonical party. Only override if this candidate runs under a different label in this specific race (e.g., party switch).
                </Form.Text>
              </Form.Group>
              <Form.Check
                type="switch"
                id="race-confirm-incumbent"
                label="Mark as incumbent in this race"
                checked={confirmIncumbent}
                onChange={(e) => setConfirmIncumbent(e.target.checked)}
                data-testid="race-confirm-incumbent-toggle"
              />
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirmRace(null)} disabled={busy}>
            Back
          </Button>
          <Button
            variant="primary"
            onClick={applyAssignment}
            disabled={busy}
            data-testid="race-confirm-apply-btn"
          >
            {busy && <Spinner size="sm" className="me-1" />}
            Confirm assignment
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
}
